import { Base64, SHA256 } from "crypto-es";
import { holdWhileTokenGrant } from "../AuthStore/devHmrReloadHold";
import { useConfigStore } from "../ConfigStore/useConfigStore";
import { api } from "./api";

export enum SessionStorageKeys {
  CODE_VERIFIER = "codeVerifier",
  STATE = "state",
}

export type PkceCallbackFailure = "state_mismatch" | "verifier_missing" | "code_missing";

/**
 * The callback cannot be exchanged in this tab: it lacks its code, its state is not the one this tab sent, or the
 * code verifier of the login is gone (another tab, a cleared session or a callback that was already handled). The
 * code is useless here, so the only way on is a new login.
 */
export class PkceCallbackError extends Error {
  constructor(readonly reason: PkceCallbackFailure) {
    super(`PKCE callback rejected: ${reason}`);
    this.name = "PkceCallbackError";
  }
}

/** Forgets the code verifier and state of the login in progress; both belong to exactly one code exchange. */
export function clearPkceSession(): void {
  sessionStorage.removeItem(SessionStorageKeys.CODE_VERIFIER);
  sessionStorage.removeItem(SessionStorageKeys.STATE);
}

export interface AuthorizationUrlOptions {
  prompt?: "login" | "select_account"
  loginHint?: string
  /** Tells the login screen this sign-in adds an account next to the ones already signed in. Carries no account data. */
  addAccount?: boolean
}

export function usePkce() {
  const { config } = useConfigStore();

  function generateCodeVerifier() {
    // Generate a random code verifier with 43-128 characters.
    const length = Math.floor(Math.random() * (128 - 43 + 1) + 43);
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
    let codeVerifier = "";
    for (let i = 0; i < length; i++) {
      codeVerifier += characters.charAt(Math.floor(Math.random() * characters.length));
    }

    return codeVerifier;
  }

  function generateCodeChallenge(codeVerifier: string) {
    // Calculate the code challenge using SHA-256.
    const hashed = SHA256(codeVerifier);
    return Base64.stringify(hashed).replace(/=/g, "").replace(/\+/g, "-").replace(/\//g, "_");
  }

  function generateRandomState() {
    // Generate a random state value.
    return Math.random().toString(36).substring(7);
  }

  function generateAuthorizationUrl(parentOrigin?: string, options: AuthorizationUrlOptions = {}) {
    console.log("[usePkce] generateAuthorizationUrl called");

    if (!config.value) {
      console.error("[usePkce] config not set.", config.value);
      throw new Error("Config not set");
    }

    console.log("[usePkce] Config values:", {
      loginClientId: config.value.loginClientId,
      loginUrl: config.value.loginUrl,
      loginRedirect: config.value.loginRedirect,
    });

    const codeVerifier = generateCodeVerifier();
    const codeChallenge = generateCodeChallenge(codeVerifier);
    const randomState = generateRandomState();

    // If parentOrigin is provided, encode it in state for secure postMessage (popup flow)
    // Otherwise, use simple state (same-window redirect flow for web/cms)
    let state: string;
    if (parentOrigin) {
      const stateData = {
        nonce: randomState,
        origin: parentOrigin,
      };
      state = btoa(JSON.stringify(stateData));
      console.log("[usePkce] Generated PKCE parameters (popup mode):", {
        codeVerifierLength: codeVerifier.length,
        codeChallenge: `${codeChallenge.substring(0, 10)}...`,
        state: randomState,
        parentOrigin: stateData.origin,
      });
    } else {
      state = randomState;
      console.log("[usePkce] Generated PKCE parameters (redirect mode):", {
        codeVerifierLength: codeVerifier.length,
        codeChallenge: `${codeChallenge.substring(0, 10)}...`,
        state: randomState,
      });
    }

    sessionStorage.setItem(SessionStorageKeys.CODE_VERIFIER, codeVerifier);
    sessionStorage.setItem(SessionStorageKeys.STATE, state);
    console.log("[usePkce] Stored code verifier and state in sessionStorage");

    const redirectUri = `${config.value.loginRedirect}/oauth/v2/callback`;
    const searchParams = new URLSearchParams();
    searchParams.append("client_id", `${config.value.loginClientId}`);
    searchParams.append("redirect_uri", redirectUri);
    searchParams.append("response_type", "code");
    searchParams.append("state", state);
    searchParams.append("code_challenge_method", "S256");
    searchParams.append("code_challenge", codeChallenge);
    if (options.prompt) {
      searchParams.append("prompt", options.prompt);
    }
    if (options.loginHint) {
      searchParams.append("login_hint", options.loginHint);
    }

    if (options.addAccount) {
      searchParams.append("add_account", "1");
    }

    const loginUrl = config.value?.loginUrl;
    if (!loginUrl) {
      console.error("[usePkce] Login URL not set");
      throw new Error("Login URL not set");
    }

    const fullUrl = `${loginUrl.toString()}/oauth/v2/authorize?${searchParams.toString()}`;
    console.log("[usePkce] Authorization URL generated:", fullUrl);

    return fullUrl;
  }

  async function exchangeAuthorizationCode(authorizationCode: string, state: string) {
    console.log("[usePkce] exchangeAuthorizationCode called");

    if (!config.value) {
      console.error("[usePkce] Config not set during exchange");
      throw new Error("Config not set");
    }

    const appConfig = config.value;
    const codeVerifier = sessionStorage.getItem(SessionStorageKeys.CODE_VERIFIER);
    const sessionState = sessionStorage.getItem(SessionStorageKeys.STATE);

    console.log("[usePkce] Session storage values:", {
      codeVerifier: codeVerifier ? `${codeVerifier.substring(0, 10)}... (length: ${codeVerifier.length})` : "missing",
      sessionState: sessionState ? `${sessionState.substring(0, 20)}...` : "missing",
      receivedState: state ? `${state.substring(0, 20)}...` : "missing",
      stateMatch: state === sessionState,
    });

    // The verifier and state belong to this one exchange: whatever happens below, a second attempt with them
    // (a refreshed callback URL) could only replay a code that is already used.
    clearPkceSession();

    if (state !== sessionState) {
      console.error("[usePkce] State mismatch!", {
        expected: sessionState ? `${sessionState.substring(0, 20)}...` : "missing",
        received: state ? `${state.substring(0, 20)}...` : "missing",
      });
      throw new PkceCallbackError("state_mismatch");
    }

    if (!codeVerifier) {
      console.error("[usePkce] Code verifier missing");
      throw new PkceCallbackError("verifier_missing");
    }

    const requestPayload = {
      grant_type: "authorization_code",
      code: `${authorizationCode.substring(0, 10)}...`,
      code_verifier: codeVerifier ? `${codeVerifier.substring(0, 10)}...` : "missing",
      client_id: appConfig.loginClientId,
      redirect_uri: `${appConfig.loginRedirect}/oauth/v2/callback`,
    };

    console.log("[usePkce] Token exchange request:", {
      url: `${appConfig.loginUrl}/oauth/v2/token`,
      payload: requestPayload,
    });

    const res = await holdWhileTokenGrant(() => api.post("/oauth/v2/token", {
      grant_type: "authorization_code",
      code: authorizationCode,
      code_verifier: codeVerifier,
      client_id: appConfig.loginClientId, // Replace with your client ID.
      redirect_uri: `${appConfig.loginRedirect}/oauth/v2/callback`, // Replace with your callback URL.
    }, { baseURL: `${appConfig.loginUrl}` }));

    console.log("[usePkce] Token exchange response:", {
      status: res.status,
      statusText: res.statusText,
      hasAccessToken: !!res.data?.access_token,
      hasRefreshToken: !!res.data?.refresh_token,
    });

    if (res.status !== 200) {
      console.error("[usePkce] Token exchange failed:", res.status, res.data);
      throw new Error("Failed to exchange authorization code with access token.");
    }

    console.log("[usePkce] Token exchange successful");
    return res.data;
  }

  function decodeState(state: string): { nonce: string, origin: string } | null {
    try {
      const decoded = JSON.parse(atob(state));
      if (decoded.nonce && decoded.origin) {
        return decoded;
      }
      return null;
    } catch (e) {
      console.error("[usePkce] Failed to decode state:", e);
      return null;
    }
  }

  return {
    exchangeAuthorizationCode,
    generateAuthorizationUrl,
    decodeState,
  };
}
